import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from '@mui/material';
import { SearchCompatibilityItem } from '@peerless/data-access/api';
import { icons } from '@peerless/ui-assets/icons';
import { palette } from '@peerless/ui-assets/palette';
import { Link } from '@peerless/ui-shared';
import { useTranslations } from 'next-intl';

interface Props {
  keyword: string;
  deviceName: string;
  displayNotFound?: boolean;
  data?: SearchCompatibilityItem;
}

export function CompatibilityCheckResult({ displayNotFound, keyword, data, deviceName }: Props) {
  const t = useTranslations();

  if (displayNotFound) {
    return (
      <Alert
        severity="info"
        icon={<FontAwesomeIcon fixedWidth icon={icons.octagonExclamation} color="info" />}
        variant="outlined"
        sx={{ my: 2 }}
      >
        {t('Search.zero-results', { keyword })}
      </Alert>
    );
  }

  if (!deviceName && !data) {
    return null;
  }

  const { accessory: requiredAccessory } = data ?? {};
  const isCompatible = !!data && !!deviceName;
  const severity = isCompatible ? 'success' : 'warning';
  const icon = isCompatible ? icons.octagonCheck : icons.octagonX;
  const iconColor = isCompatible ? palette.success.main : palette.warning.main;
  const getCompatibleMessage = (accessory?: string | null) => {
    return accessory
      ? t.rich('Product.device-is-compatible-with-accessory', {
          deviceName,
          a: () => <AccessoryLink sku={accessory} />,
        })
      : t('Product.device-is-compatible-text', { deviceName });
  };

  const message = isCompatible
    ? getCompatibleMessage(requiredAccessory)
    : t('Product.device-not-compatible-text', { deviceName });

  return (
    <Alert
      severity={severity}
      icon={<FontAwesomeIcon fixedWidth icon={icon} color={iconColor} />}
      variant="outlined"
      sx={{ my: 2 }}
    >
      {message}
    </Alert>
  );
}

function AccessoryLink({ sku }: { sku: string }) {
  return (
    <Link target="_blank" href={`/products/${sku}`}>
      {sku}
    </Link>
  );
}
